import Layout from "@/components/layout/Layout";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingCart, Loader2, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  ShippingRateSelector,
  type ShippingRate,
  type ShippingAddress,
} from "@/components/cart/ShippingRateSelector";

// Rough per-unit weight estimate (lbs). Letter sets are heavy (100 chars);
// accessories are lighter. Refine with real per-SKU weights later.
const ESTIMATED_LB_PER_UNIT = 3;

export default function Cart() {
  const { items, removeItem, updateQuantity, totalPrice } = useCart();
  const [loading, setLoading] = useState(false);
  const [selectedRate, setSelectedRate] = useState<ShippingRate | null>(null);
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress | null>(null);
  const { toast } = useToast();

  const totalWeight = useMemo(
    () =>
      Math.max(
        items.reduce((sum, i) => sum + i.quantity * ESTIMATED_LB_PER_UNIT, 0),
        0.5,
      ),
    [items],
  );

  const shippingCents = selectedRate
    ? Math.round(parseFloat(selectedRate.amount) * 100)
    : 0;
  const orderTotal = totalPrice + shippingCents;

  const handleCheckout = async () => {
    if (!selectedRate || !shippingAddress) {
      toast({
        title: "Select a shipping option",
        description: "Please verify your address and choose a shipping rate first.",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      // Send ONLY stable identifiers — the checkout function looks up
      // prices and names from the authoritative server-side catalog.
      const cartItems = items.map((item) => ({
        variantId: item.variantId,
        colorLabel: item.color ?? undefined,
        quantity: item.quantity,
      }));

      const shipping = {
        label: `Shipping: ${selectedRate.carrier} ${selectedRate.service}`,
        amountCents: Math.round(parseFloat(selectedRate.amount) * 100),
      };

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { items: cartItems, shipping, shippingAddress },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      toast({ title: "Checkout failed", description: err.message || "Something went wrong.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <Layout>
        <div className="container flex flex-col items-center py-24 text-center">
          <ShoppingCart className="h-16 w-16 text-muted-foreground/40" />
          <h1 className="mt-6 font-display text-3xl font-bold">Your cart is empty</h1>
          <p className="mt-2 text-muted-foreground">Add some products to get started.</p>
          <Button asChild className="mt-6 bg-accent text-accent-foreground hover:bg-accent/90">
            <Link to="/shop">Browse Products</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-12">
        <h1 className="font-display text-3xl font-bold">Shopping Cart</h1>

        <div className="mt-8 grid gap-10 lg:grid-cols-3">
          {/* Cart items */}
          <div className="space-y-4 lg:col-span-2">
            {items.map((item, index) => (
              <div key={index} className="flex items-center gap-4 rounded-lg border p-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-secondary">
                  <img src={item.image} alt={item.name} className="h-10 w-10 object-contain opacity-30" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold">{item.name}</h3>
                  <p className="text-xs text-muted-foreground">
                    {[item.size, item.color, item.variant].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateQuantity(index, item.quantity - 1)}>
                    <Minus className="h-3 w-3" />
                  </Button>
                  <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateQuantity(index, item.quantity + 1)}>
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
                <p className="w-20 text-right font-semibold">${((item.price * item.quantity) / 100).toFixed(2)}</p>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeItem(index)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}

            {/* Shipping info */}
            <div className="rounded-lg border p-5 space-y-4">
              <ShippingRateSelector
                onRateSelect={setSelectedRate}
                onAddressConfirmed={setShippingAddress}
                selectedRate={selectedRate}
                totalWeight={totalWeight}
              />

              {/* Expedited CTA */}
              <div className="rounded-lg bg-muted/50 p-4 flex items-start gap-3">
                <Phone className="h-4 w-4 mt-0.5 shrink-0 text-accent" />
                <div>
                  <p className="text-sm font-semibold">Need it faster?</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    For 2nd Day Air or Next Day Air, call Nick directly and we'll rush your order immediately.
                  </p>
                  <a
                    href="tel:+13143698649"
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:underline"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    314-369-8649
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Order summary */}
          <div className="rounded-lg border bg-card p-6 h-fit">
            <h3 className="font-display text-lg font-semibold">Order Summary</h3>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>${(totalPrice / 100).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shipping</span>
                <span>
                  {selectedRate
                    ? `$${(shippingCents / 100).toFixed(2)}`
                    : "Calculate below"}
                </span>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between border-t pt-4">
              <span className="font-semibold">Total</span>
              <span className="text-xl font-bold">${(orderTotal / 100).toFixed(2)}</span>
            </div>
            <Button
              className="mt-6 w-full bg-accent text-accent-foreground hover:bg-accent/90"
              size="lg"
              onClick={handleCheckout}
              disabled={loading || !selectedRate}
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Redirecting…</>
              ) : (
                "Proceed to Checkout"
              )}
            </Button>
            <p className="mt-3 text-center text-xs text-muted-foreground">Secure payment powered by Stripe</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
